import { useState, useEffect, useCallback } from 'react';
import {
  supabase,
  isSupabaseConfigured,
  transformTaskToCamelCase,
  transformTaskToSnakeCase,
} from '../utils/supabase';
import { auth } from '../utils/init-firebase';

/**
 * Helper function to recalculate and update a deal's next_action_due field
 * based on the earliest pending task's due date.
 * 
 * This is called after task completion, deletion, reopening, or creation
 * to keep the deal's next_action_due in sync with its tasks.
 * 
 * @param {string} dealId - The UUID of the deal to update
 * @returns {Promise<void>}
 */
async function updateDealNextActionDue(dealId) {
  if (!dealId || !isSupabaseConfigured()) return;

  try {
    // Find the earliest pending/in-progress task for this deal
    const { data: nextTask, error: queryError } = await supabase
      .from('crm_tasks')
      .select('due_date')
      .eq('deal_id', dealId)
      .in('status', ['pending', 'in-progress'])
      .not('due_date', 'is', null)
      .order('due_date', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (queryError) {
      console.error('[updateDealNextActionDue] Error querying next task:', queryError);
      return;
    }

    // Update the deal's next_action_due
    const { error: updateError } = await supabase
      .from('crm_deals')
      .update({
        next_action_due: nextTask?.due_date || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', dealId);

    if (updateError) {
      console.error('[updateDealNextActionDue] Error updating deal:', updateError);
    } else {
      console.log(`[updateDealNextActionDue] Updated deal ${dealId} next_action_due to:`, nextTask?.due_date || 'null');
    }
  } catch (error) {
    console.error('[updateDealNextActionDue] Unexpected error:', error);
  }
}

// Task row transforms live in utils/supabase.js (shared with useDealTasks) so
// the two hooks can never drift apart on field mapping again.

export function useTasks(userIdOrIds = null, filters = {}) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Normalize to array for consistent handling. An empty array is a valid
  // "view all" filter (admin mode), so it must never be used as an auth check.
  const userIds = Array.isArray(userIdOrIds) 
    ? userIdOrIds.filter(Boolean) 
    : userIdOrIds ? [userIdOrIds] : [];

  // status/type accept a single value or an array. Dependencies below use the
  // stringified lists so callers can pass inline array literals without the
  // changing identity re-triggering fetch/subscribe on every render.
  const statusList = filters.status
    ? (Array.isArray(filters.status) ? filters.status : [filters.status])
    : [];
  const typeList = filters.type
    ? (Array.isArray(filters.type) ? filters.type : [filters.type])
    : [];
  const statusKey = JSON.stringify(statusList);
  const typeKey = JSON.stringify(typeList);

  // Fetch tasks via the crm_get_user_tasks RPC, which resolves
  // "assigned to me OR on a deal I own" server-side. Doing that OR client-side
  // required inlining every owned deal UUID into the request URL, which exceeded
  // the gateway's URL limit for users owning many deals and returned no tasks.
  // `silent` skips the loading flag so realtime-triggered refetches don't
  // blank pages that render a spinner while loading (e.g. TasksPage).
  const fetchTasks = useCallback(async ({ silent = false } = {}) => {
    if (!isSupabaseConfigured()) {
      console.warn('[useTasks] Supabase is not configured');
      setTasks([]);
      setError(null);
      setLoading(false);
      return;
    }

    if (!silent) setLoading(true);

    try {
      // An empty p_user_ids array means "view all" mode (for admins).
      // The RPC always returns the FULL active (pending/in-progress) set;
      // p_limit only windows completed history (most recent first).
      const { data, error: rpcError } = await supabase.rpc('crm_get_user_tasks', {
        p_user_ids: userIds,
        p_types: typeList,
        p_statuses: statusList,
        p_limit: 250,
      });

      if (rpcError) {
        console.error('[useTasks] Error fetching tasks:', rpcError);
        setTasks([]);
        setError(rpcError.message || 'Failed to load tasks');
        setLoading(false);
        return;
      }

      // Transform rows to camelCase
      const taskList = (data || []).map(row => transformTaskToCamelCase(row));

      setTasks(taskList);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('[useTasks] ❌ Unexpected error:', err);
      setTasks([]);
      setError(err.message || 'Failed to load tasks');
      setLoading(false);
    }
  }, [JSON.stringify(userIds), statusKey, typeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set up real-time subscription
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setTasks([]);
      setLoading(false);
      return;
    }

    // Allow empty userIds for "view all" mode (admins)
    // If userIds is empty, we'll fetch all tasks without user filter

    // Initial fetch
    fetchTasks();

    // Debounced, silent refetch for realtime events. The channel listens to
    // every crm_tasks change (no filter), so an automation burst completing N
    // tasks would otherwise fire N back-to-back full refetches — one trailing
    // fetch after the burst settles is enough. Silent so the page keeps
    // showing (stale-for-a-moment) data instead of a loading spinner.
    let refetchTimer = null;
    const scheduleRefetch = () => {
      if (refetchTimer) clearTimeout(refetchTimer);
      refetchTimer = setTimeout(() => {
        fetchTasks({ silent: true });
      }, 500);
    };

    // Subscribe to real-time updates for tasks
    // NOTE: We don't filter by assigned_to here because we now also show tasks
    // linked to deals the user owns (even if assigned to someone else).
    // The fetchTasks function handles the proper OR filtering logic.
    const channel = supabase
      .channel(`crm_tasks_changes-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_tasks',
          // No filter - we let fetchTasks handle the filtering to include
          // both assigned tasks AND tasks on owned deals
        },
        scheduleRefetch
      )
      .subscribe();

    return () => {
      if (refetchTimer) clearTimeout(refetchTimer);
      supabase.removeChannel(channel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(userIds), statusKey, typeKey]);

  const createTask = async (taskData) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    if (!auth.currentUser) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      // Ensure required fields are set
      // TaskModal passes assignedBy but we need createdBy for the database
      const createdBy = taskData.createdBy || taskData.assignedBy || userIds[0] || auth.currentUser.uid;
      const assignedTo = taskData.assignedTo || userIds[0] || auth.currentUser.uid;
      
      const newTask = transformTaskToSnakeCase({
        ...taskData,
        createdBy,
        assignedTo,
        status: taskData.status || 'pending',
        priority: taskData.priority || 'medium',
        source: taskData.source || 'manual',
      });

      const { data, error } = await supabase
        .from('crm_tasks')
        .insert(newTask)
        .select('*')
        .single();

      if (error) {
        console.error('[useTasks] Error creating task:', error);
        return { success: false, error: error.message };
      }

      // Immediately update local state with the new task
      const newTaskCamelCase = transformTaskToCamelCase(data);
      setTasks(prev => [...prev, newTaskCamelCase].sort((a, b) => {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(b.dueDate) - new Date(a.dueDate); // desc order
      }));

      // Update the deal's next_action_due if this task is linked to a deal
      if (data.deal_id) {
        await updateDealNextActionDue(data.deal_id);
      }

      return { success: true, id: data.id };
    } catch (error) {
      console.error('[useTasks] Error creating task:', error);
      return { success: false, error: error.message };
    }
  };

  const updateTask = async (taskId, updates) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    if (!auth.currentUser) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      // Check if we need to recalculate deal's next_action_due
      const shouldUpdateDeal = updates.dueDate !== undefined || updates.status !== undefined;
      let dealId = null;

      if (shouldUpdateDeal) {
        // Get the task's deal_id
        const { data: taskData } = await supabase
          .from('crm_tasks')
          .select('deal_id')
          .eq('id', taskId)
          .single();
        dealId = taskData?.deal_id;
      }

      const updateData = transformTaskToSnakeCase(updates);
      updateData.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from('crm_tasks')
        .update(updateData)
        .eq('id', taskId);

      if (error) {
        console.error('[useTasks] Error updating task:', error);
        return { success: false, error: error.message };
      }

      // Immediately update local state
      setTasks(prev => prev.map(t => 
        t.id === taskId 
          ? { ...t, ...updates }
          : t
      ));

      // Update the deal's next_action_due if due date or status changed
      if (dealId) {
        await updateDealNextActionDue(dealId);
      }

      return { success: true };
    } catch (error) {
      console.error('[useTasks] Error updating task:', error);
      return { success: false, error: error.message };
    }
  };

  const completeTask = async (taskId) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    if (!auth.currentUser) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      // Get the task's deal_id for next_action_due recalculation
      const { data: taskData, error: fetchError } = await supabase
        .from('crm_tasks')
        .select('deal_id')
        .eq('id', taskId)
        .single();

      if (fetchError) {
        console.error('[useTasks] Error fetching task for completion:', fetchError);
        return { success: false, error: fetchError.message };
      }

      // Complete the task. If it was synced to Google Calendar, the DB trigger
      // enqueues the removal and the server takes it off the calendar.
      const completedAt = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('crm_tasks')
        .update({
          status: 'completed',
          completed_at: completedAt,
          updated_at: new Date().toISOString(),
        })
        .eq('id', taskId);

      if (updateError) {
        console.error('[useTasks] Error completing task:', updateError);
        return { success: false, error: updateError.message };
      }

      // Immediately update local state
      setTasks(prev => prev.map(t => 
        t.id === taskId 
          ? { ...t, status: 'completed', completedAt: new Date(completedAt) }
          : t
      ));

      // Update the deal's next_action_due if this task is linked to a deal
      if (taskData?.deal_id) {
        await updateDealNextActionDue(taskData.deal_id);
      }

      return { success: true };
    } catch (error) {
      console.error('[useTasks] Error completing task:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteTask = async (taskId) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    if (!auth.currentUser) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      // First, get the task's deal_id before deleting
      const { data: taskData, error: fetchError } = await supabase
        .from('crm_tasks')
        .select('deal_id')
        .eq('id', taskId)
        .single();

      if (fetchError) {
        console.error('[useTasks] Error fetching task for deletion:', fetchError);
        // Continue with deletion even if fetch fails
      }

      const dealId = taskData?.deal_id;

      const { error } = await supabase
        .from('crm_tasks')
        .delete()
        .eq('id', taskId);

      if (error) {
        console.error('[useTasks] Error deleting task:', error);
        return { success: false, error: error.message };
      }

      // Immediately remove from local state
      setTasks(prev => prev.filter(t => t.id !== taskId));

      // Update the deal's next_action_due if this task was linked to a deal
      if (dealId) {
        await updateDealNextActionDue(dealId);
      }

      return { success: true };
    } catch (error) {
      console.error('[useTasks] Error deleting task:', error);
      return { success: false, error: error.message };
    }
  };

  const reopenTask = async (taskId) => {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase not configured' };
    }

    if (!auth.currentUser) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      // First, get the task's deal_id
      const { data: taskData, error: fetchError } = await supabase
        .from('crm_tasks')
        .select('deal_id')
        .eq('id', taskId)
        .single();

      if (fetchError) {
        console.error('[useTasks] Error fetching task for reopening:', fetchError);
        // Continue with reopening even if fetch fails
      }

      const { error } = await supabase
        .from('crm_tasks')
        .update({
          status: 'pending',
          completed_at: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', taskId);

      if (error) {
        console.error('[useTasks] Error reopening task:', error);
        return { success: false, error: error.message };
      }

      // Immediately update local state
      setTasks(prev => prev.map(t => 
        t.id === taskId 
          ? { ...t, status: 'pending', completedAt: null }
          : t
      ));

      // Update the deal's next_action_due if this task is linked to a deal
      if (taskData?.deal_id) {
        await updateDealNextActionDue(taskData.deal_id);
      }

      return { success: true };
    } catch (error) {
      console.error('[useTasks] Error reopening task:', error);
      return { success: false, error: error.message };
    }
  };

  return {
    tasks,
    loading,
    error,
    createTask,
    updateTask,
    completeTask,
    deleteTask,
    reopenTask,
    refetch: fetchTasks,
  };
}

// Export utilities for use elsewhere (transforms come from utils/supabase.js)
export { updateDealNextActionDue };
